// TSIA2 answer review, tutor side.
//
// The student page, reading the tutor endpoint, so a tutor sees exactly what
// their student sees -- including the highlights the student made.
//
// Mirrors DigitalSHSATReview's actual UI rather than approximating it: the
// section-select title with its chevron, the dashed rule, the split pane with
// the passage left and the question right, radio-button choices tinted by
// outcome, the explanation in a popup, and the fixed footer bar carrying
// Question N/M with a navigator bubble and BACK / NEXT.
//
// Dropped, because TSIA2 has none of them: the timer, the marked-for-review
// bookmark and its legend, the glossary popup, and every question type other
// than multiple choice.
//
// Two things are TSIA2's own. The student's highlights are redrawn over the
// passage and stem from stored character offsets. And the navigator groups by
// stage, because CRC and Diagnostic numbering both start at 1 -- a single run
// would name the wrong question.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineLocationOn,
} from 'react-icons/md';
import { IoClose } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import renderMathInElement from 'katex/dist/contrib/auto-render.js';
import { applyKatexWrap, flattenKatexUnderlines } from 'global/katexWrap';
import 'katex/dist/katex.min.css';

import Button from 'components/Button';
import Loader from 'components/Loader';
import axios from 'config/axios';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';

import { HighlightLayers, TSIA2Annotation } from './highlights';
import { useDraggable } from './useDraggable';

import './index.css';

// Declared here because the tutor app keeps this per file rather than in
// a global module.
const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D'];

type TSIA2Figure = { figureId: string; url: string | null };

type TSIA2Choice = {
    index: number;
    label: string | null;
    text: string | null;
    figure: TSIA2Figure | null;
};

type TSIA2Question = {
    slot: number;
    questionId: string;
    instructions: string | null;
    calculatorMode: 'basic' | 'sqrt' | 'graphing' | null;
    question: string | null;
    passage: { passageId: string; introduction: string | null; passage: string } | null;
    figures: TSIA2Figure[];
    choices: TSIA2Choice[];
    savedChoiceIndex: number | null;
    annotations: TSIA2Annotation[];
};

type ReviewChoice = TSIA2Choice & { isCorrect: boolean };

type ReviewQuestion = Omit<TSIA2Question, 'choices'> & {
    choices: ReviewChoice[];
    explanation: string | null;
    isCorrect: boolean;
    category: string | null;
    moduleName: string;
};

type ReviewSection = {
    sectionId: number;
    sectionName: string;
    questions: ReviewQuestion[];
};

type Review = { testName: string; studentName?: string; sections: ReviewSection[] };

const KATEX_OPTIONS = {
    delimiters: [
        { left: '$$', right: '$$', display: true },
        { left: '$', right: '$', display: false },
        { left: '\\(', right: '\\)', display: false },
        { left: '\\[', right: '\\]', display: true },
    ],
    trust: true,
};

function Katex({
    content, className, innerRef,
}: {
    content: string | null;
    className?: string;
    innerRef?: React.RefObject<HTMLDivElement>;
}) {
    const ownRef = useRef<HTMLDivElement>(null);
    const ref = innerRef ?? ownRef;
    useEffect(() => {
        if (!ref.current) return;
        // Authored text, not markup -- set as text so a "<" in a math
        // inequality cannot open a tag, exactly as the player does. The two
        // must agree, because highlight offsets saved against the player's
        // rendering are replayed against this one.
        ref.current.textContent = content ?? '';
        renderMathInElement(ref.current, KATEX_OPTIONS);
        ref.current.innerHTML = ref.current.innerHTML.replaceAll('&nbsp;', ' ');
        // KaTeX renders each base as one unbreakable run, so a passage or an
        // intro authored as $\text{...}$ would refuse to wrap and scroll the
        // pane sideways. The shared helper marks those bases wrappable; it is
        // what every other player calls after rendering.
        applyKatexWrap(ref.current, { enabled: true });
        // Same fix as the player: a wrapped KaTeX underline paints its lines
        // on top of each other, so it is flattened to text (italics kept).
        flattenKatexUnderlines(ref.current);
    }, [content, ref]);
    return <div ref={ref} className={className} />;
}

function TestTSIA2Review() {
    const { testId } = useParams();
    const navigate = useNavigate();

    const [review, setReview] = useState<Review | null>(null);
    const [sectionIndex, setSectionIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [sectionSelectOpen, setSectionSelectOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [explanationOpen, setExplanationOpen] = useState(false);
    // Off by default and toggled by the student, exactly as the other reviews
    // do it: with it off, only their own answer is marked, so they can re-read
    // the question before being told the key.
    const [showAnswer, setShowAnswer] = useState(false);
    // Bumped once the text is on the page, so the highlight layers are measured
    // against rendered markup rather than what preceded it.
    const [layout, setLayout] = useState(0);

    const passageRef = useRef<HTMLDivElement>(null);
    const stemRef = useRef<HTMLDivElement>(null);
    // The popup header is styled `cursor: grab` like the other players'; this
    // is what makes that true.
    const explanationDrag = useDraggable<HTMLDivElement>();
    // The hook owns the ref and hands it back, so it attaches to the element
    // that should stay open when clicked.
    const navigatorRef = useOutsideClickHandler(() => setNavigatorOpen(false));
    const sectionSelectRef = useOutsideClickHandler(() => setSectionSelectOpen(false));

    useEffect(() => {
        let cancelled = false;
        // Clear the previous attempt's review when the URL changes underneath
        // a mounted page, rather than show it while the next one loads.
        setReview(null);
        axios.get(`tests/digital/tsia2/tutor/review/${testId}/`)
            .then(response => { if (!cancelled) setReview(response.data); })
            .catch(error => {
                if (cancelled) return;
                console.error(error);
                navigate(-1);
            });
        return () => { cancelled = true; };
    }, [testId, navigate]);

    const section = review?.sections[sectionIndex] ?? null;
    const question = section?.questions[questionIndex] ?? null;
    const total = section?.questions.length ?? 0;

    useEffect(() => { setLayout(value => value + 1); }, [question?.questionId]);

    // Stages in the order they were sat, each numbered from 1.
    const stages = useMemo(() => {
        if (!section) return [];
        const order: string[] = [];
        const byStage = new Map<string, { question: ReviewQuestion; index: number }[]>();
        section.questions.forEach((item, index) => {
            if (!byStage.has(item.moduleName)) {
                byStage.set(item.moduleName, []);
                order.push(item.moduleName);
            }
            byStage.get(item.moduleName)!.push({ question: item, index });
        });
        return order.map(name => ({ name, items: byStage.get(name)! }));
    }, [section]);

    const annotations = (question?.annotations ?? []) as TSIA2Annotation[];
    const passageAnnotations = annotations.filter(item => item.passageId !== null);
    const stemAnnotations = annotations.filter(item => item.passageId === null);

    // Position within the stage, which is the number the student saw.
    const positionInStage = useMemo(() => {
        for (const stage of stages) {
            const at = stage.items.findIndex(item => item.index === questionIndex);
            if (at !== -1) return { stage: stage.name, position: at + 1, of: stage.items.length };
        }
        return { stage: '', position: questionIndex + 1, of: total };
    }, [stages, questionIndex, total]);

    // Ungated on the tutor side, matching the tutor's other reviews: those
    // customizations govern what a STUDENT may see, not their tutor.
    const canShowAnswer = true;
    const canShowExplanation = true;


    // BACK and NEXT walk the whole attempt, not just the section on screen:
    // running off the end of ELAR steps into Math, and back off the start of
    // Math returns to ELAR's last question. Only the very first and very last
    // questions of the whole review disable a button.
    const atVeryStart = sectionIndex === 0 && questionIndex === 0;
    const atVeryEnd = (
        sectionIndex === (review?.sections.length ?? 1) - 1
        && questionIndex >= total - 1
    );

    const goBack = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
            return;
        }
        if (sectionIndex === 0 || !review) return;
        const previous = sectionIndex - 1;
        setSectionIndex(previous);
        setQuestionIndex(Math.max(review.sections[previous].questions.length - 1, 0));
    };

    const goNext = () => {
        if (questionIndex < total - 1) {
            setQuestionIndex(questionIndex + 1);
            return;
        }
        if (!review || sectionIndex >= review.sections.length - 1) return;
        setSectionIndex(sectionIndex + 1);
        setQuestionIndex(0);
    };

    if (!review) return <Loader />;

    return (
        <div className='digitalTSIA2ReviewBox tsia2TutorPalette'>
            <Helmet><title>{review.testName} Review</title></Helmet>
            <div className='digitalTSIA2Review'>

            {explanationOpen && question?.explanation && (
                <div
                    className='digitalTSIA2Review__popup--explanation'
                    ref={explanationDrag.ref}
                >
                    <div
                        className='digitalTSIA2Review__popupHeader'
                        onMouseDown={explanationDrag.onHeaderMouseDown}
                    >
                        <h3 className='digitalTSIA2Review__popupHeaderText'>Explanation</h3>
                        <IoClose
                            className='digitalTSIA2Review__popupHeaderCloseBig'
                            onClick={() => setExplanationOpen(false)}
                        />
                    </div>
                    <div className='digitalTSIA2Review__explanationContainer'>
                        <div className='digitalTSIA2Review__explanationBox'>
                            <Katex content={question.explanation} />
                        </div>
                    </div>
                </div>
            )}

            <div className='digitalTSIA2Review__header'>
                <div className='digitalTSIA2Review__titleGroup' ref={sectionSelectRef}>
                    <button
                        className='digitalTSIA2Review__moduleSelect'
                        onClick={() => setSectionSelectOpen(!sectionSelectOpen)}
                    >
                        <h1 className='digitalTSIA2Review__title'>
                            {section?.sectionName ?? review.testName}
                        </h1>
                        {sectionSelectOpen
                            ? <MdKeyboardArrowUp className='digitalTSIA2Review__moduleSelectIcon' />
                            : <MdKeyboardArrowDown className='digitalTSIA2Review__moduleSelectIcon' />}
                    </button>
                    {sectionSelectOpen && (
                        <div className='digitalTSIA2Review__moduleSelectDropdown'>
                            {review.sections.map((item, index) => (
                                <button
                                    key={item.sectionId}
                                    // The ported stylesheet styles
                                    // `moduleSelectDropdownItem`; the name I had
                                    // matched no rule, so these rows fell back to
                                    // default button styling -- centred, unpadded
                                    // and with no hover.
                                    className={index === sectionIndex
                                        ? 'digitalTSIA2Review__moduleSelectDropdownItem--selected'
                                        : 'digitalTSIA2Review__moduleSelectDropdownItem'}
                                    onClick={() => {
                                        setSectionIndex(index);
                                        setQuestionIndex(0);
                                        setSectionSelectOpen(false);
                                    }}
                                >
                                    {item.sectionName}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className='digitalTSIA2Review__actionGroup'>
                    <button
                        className='digitalTSIA2Review__action'
                        onClick={() => navigate(-1)}
                    >
                        <MdExitToApp className='digitalTSIA2Review__actionIcon' />
                        <p className='digitalTSIA2Review__actionText'>Exit</p>
                    </button>
                </div>
            </div>
            <div className='digitalTSIA2Review__dottedLine' />

            {question && (
                <div className='digitalTSIA2Review__test'>
                    {question.passage && (<>
                        <div className='digitalTSIA2Review__material'>
                            <div className='digitalTSIA2Review__materialContent'>
                                {question.passage.introduction && (
                                    <Katex
                                        content={question.passage.introduction}
                                        className='digitalTSIA2Review__passageIntro'
                                    />
                                )}
                                <div className='digitalTSIA2Review__highlightable'>
                                    <Katex
                                        content={question.passage.passage}
                                        className='digitalTSIA2Review__passageText'
                                        innerRef={passageRef}
                                    />
                                    <HighlightLayers
                                        target={passageRef}
                                        annotations={passageAnnotations}
                                        layout={layout}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='digitalTSIA2Review__verticalLine' />
                    </>)}

                    <div className='digitalTSIA2Review__question'>
                        <div className='digitalTSIA2Review__questionContent'>
                            <div className='digitalTSIA2Review__questionHeader'>
                                <p className={question.isCorrect
                                    ? 'digitalTSIA2Review__questionNumber--correct'
                                    : 'digitalTSIA2Review__questionNumber--wrong'}
                                >
                                    {positionInStage.position}
                                </p>
                                <p className='digitalTSIA2Review__questionMeta'>
                                    {question.moduleName}
                                    {question.category ? ` · ${question.category}` : ''}
                                </p>
                                <div className='digitalTSIA2Review__questionHeaderActions'>
                                    {canShowExplanation && question.explanation && (
                                        <button
                                            className={explanationOpen
                                                ? 'digitalTSIA2Review__showExplanation--active'
                                                : 'digitalTSIA2Review__showExplanation'}
                                            onClick={() => setExplanationOpen(!explanationOpen)}
                                        >
                                            Explanation
                                        </button>
                                    )}
                                    {canShowAnswer && (
                                        <button
                                            className={showAnswer
                                                ? 'digitalTSIA2Review__showAnswer--active'
                                                : 'digitalTSIA2Review__showAnswer'}
                                            onClick={() => setShowAnswer(!showAnswer)}
                                        >
                                            {showAnswer ? 'Hide answer' : 'Show answer'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {question.instructions && (
                                <p className='digitalTSIA2Review__instructions'>
                                    {question.instructions}
                                </p>
                            )}

                            <div className='digitalTSIA2Review__questionChoices'>
                                <div className='digitalTSIA2Review__highlightable'>
                                    <Katex
                                        content={question.question}
                                        className='digitalTSIA2Review__questionText'
                                        innerRef={stemRef}
                                    />
                                    <HighlightLayers
                                        target={stemRef}
                                        annotations={stemAnnotations}
                                        layout={layout}
                                    />
                                </div>

                                {question.figures.map(figure => (
                                    figure.url && (
                                        <img
                                            key={figure.figureId}
                                            src={figure.url}
                                            alt=''
                                            className='digitalTSIA2Review__figure'
                                        />
                                    )
                                ))}

                                <div className='digitalTSIA2Review__questionContainer'>
                                    {question.choices.map((choice, i) => {
                                        const picked = question.savedChoiceIndex === choice.index;
                                        // Same states as the other reviews: the student's own choice is
                                        // outlined -- black if right, red if wrong -- and revealing the
                                        // answer turns the key green.
                                        const state = showAnswer && choice.isCorrect
                                            ? '--correct'
                                            : picked
                                            ? (choice.isCorrect ? '--selected' : '--wrong')
                                            : '';
                                        return (
                                            <div
                                                key={choice.index}
                                                className='digitalTSIA2Review__questionChoiceWrapper'
                                            >
                                                <button
                                                    className={`digitalTSIA2Review__questionChoice${state}`}
                                                    onClick={() => {}}
                                                >
                                                    <div className='digitalTSIA2Review__questionChoiceOutline' />
                                                    <p className='digitalTSIA2Review__questionChoiceLetter'>
                                                        {MULTIPLE_CHOICES[i]}
                                                    </p>
                                                    {choice.figure?.url
                                                        ? <img
                                                            className='digitalTSIA2Review__questionChoiceFigure'
                                                            src={choice.figure.url}
                                                            alt=''
                                                            draggable={false}
                                                        />
                                                        : <span className='digitalTSIA2Review__questionChoiceTextWrapper'>
                                                            <Katex
                                                                content={choice.text}
                                                                className='digitalTSIA2Review__questionChoiceText'
                                                            />
                                                        </span>}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className='digitalTSIA2Review__dottedLine' />

            <div className='digitalTSIA2Review__footer'>
                <p className='digitalTSIA2Review__tester'>{review.studentName ?? review.testName}</p>
                <button
                    className='digitalTSIA2Review__navigatorToggle'
                    onClick={() => setNavigatorOpen(!navigatorOpen)}
                >
                    Question {positionInStage.position}/{positionInStage.of}
                    {navigatorOpen
                        ? <MdKeyboardArrowDown className='digitalTSIA2Review__navigatorToggleIcon' />
                        : <MdKeyboardArrowUp className='digitalTSIA2Review__navigatorToggleIcon' />}
                </button>
                <div className='digitalTSIA2Review__buttons'>
                    <Button
                        className='digitalTSIA2Review__button'
                        secondary
                        disabled={atVeryStart}
                        onClick={goBack}
                    >
                        BACK
                    </Button>
                    <Button
                        className='digitalTSIA2Review__button'
                        disabled={atVeryEnd}
                        onClick={goNext}
                    >
                        NEXT
                    </Button>
                </div>

                {navigatorOpen && (
                    <div className='digitalTSIA2Review__navigator' ref={navigatorRef}>
                        <div className='digitalTSIA2Review__navigatorBubble'>
                            <h2 className='digitalTSIA2Review__navigatorTitle'>
                                {section?.sectionName} Questions
                            </h2>
                            <div className='digitalTSIA2Review__navigatorLegends'>
                                <div className='digitalTSIA2Review__navigatorLegend'>
                                    <MdOutlineLocationOn className='digitalTSIA2Review__navigatorLegendCurrent' />
                                    <p className='digitalTSIA2Review__navigatorLegendText'>Current</p>
                                </div>
                                <div className='digitalTSIA2Review__navigatorLegend'>
                                    <div className='digitalTSIA2Review__navigatorLegendCorrect' />
                                    <p className='digitalTSIA2Review__navigatorLegendText'>Correct</p>
                                </div>
                                <div className='digitalTSIA2Review__navigatorLegend'>
                                    <div className='digitalTSIA2Review__navigatorLegendWrong' />
                                    <p className='digitalTSIA2Review__navigatorLegendText'>Incorrect</p>
                                </div>
                            </div>
                            {/* Grouped by stage: both stages number from 1, so one
                                continuous run would name the wrong question. */}
                            {stages.map(stage => (
                                <div className='digitalTSIA2Review__navigatorStage' key={stage.name}>
                                    <h3 className='digitalTSIA2Review__navigatorStageName'>
                                        {stage.name}
                                    </h3>
                                    <div className='digitalTSIA2Review__navigatorItems'>
                                        {stage.items.map((item, position) => (
                                            <button
                                                key={item.question.questionId}
                                                className={item.question.isCorrect
                                                    ? 'digitalTSIA2Review__navigatorItem--correct'
                                                    : 'digitalTSIA2Review__navigatorItem--wrong'}
                                                onClick={() => {
                                                    setQuestionIndex(item.index);
                                                    setNavigatorOpen(false);
                                                }}
                                            >
                                                {questionIndex === item.index
                                                    ? <u className='digitalTSIA2Review__navigatorItemNumber'>
                                                        {position + 1}
                                                    </u>
                                                    : <p className='digitalTSIA2Review__navigatorItemNumber'>
                                                        {position + 1}
                                                    </p>}
                                                {questionIndex === item.index && (
                                                    <MdOutlineLocationOn className='digitalTSIA2Review__navigatorItemCurrent' />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    );
}

export default TestTSIA2Review;
